import { useEffect, useMemo, useRef, useState } from "react";
import { HoldRowButton } from "./HoldRowButton";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Clock,
  Download,
  LockOpen,
  PenLine,
  RotateCw,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { EditAction, ViewAction } from "@/components/table/actions";
import { PermissionButton } from "@/components/ui/permission-button";
import { PermissionGuard } from "@/components/permission-guard";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import FilterButton from "@/components/table/FilterButton";
import FiltersModal from "@/components/FiltersModal";
import { useEarningsParams, type IEarningsFilters } from "./earnings.params";
import { Table, type ColumnDef } from "@/components/table";
import { cn } from "@/utils/cn";
import { ROUTES } from "@/constants/routes";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  useListItemsQuery,
  useLazyListItemsQuery,
  useMarkItemsPaidMutation,
  useReleaseItemMutation,
  useReprocessHeldMutation,
  useRequestReceiptSignaturesMutation,
  type IPayrollRunItem,
} from "@/services/(company)/payrollRunsApi";
import { generateEarningsPdf } from "@/utils/generate-earnings-pdf";
import { useDownloadPayslip } from "@/services/(company)/payslipDownload";
import { usePayrollRunSocket } from "@/services/websocket/usePayrollRunSocket";
import {
  EmployeePayrollStatusEnum,
  PAYMENT_METHOD_LABELS,
  PaidStatusEnum,
  PaymentMethodTypeEnum,
} from "@/utils/enums/payroll.enum";
import { formatDateTime } from "@/utils/helper";
import type { IOvertimeRequest } from "@/types/overtime";
import { OvertimeOverrideDialog } from "./OvertimeOverrideDialog";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : value;
  return Number.isFinite(num) ? num : null;
};

const formatNumber = (num: number) =>
  num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

// Color-code payment methods for quick scanning.
const PAYMENT_METHOD_VARIANT: Record<
  string,
  "primary" | "info" | "warning" | "success" | "neutral"
> = {
  WPS: "primary",
  BANK_TRANSFER: "info",
  CASH: "warning",
  CHEQUE: "neutral",
};

const Cell = ({
  value,
  prefix,
  tone,
}: {
  value: string | number | null;
  prefix?: string;
  tone?: "bonus" | "deduction" | "contrib";
}) => {
  if (value === null || value === undefined || value === "")
    return <span className="text-gray-300">—</span>;
  const tint =
    tone === "bonus"
      ? "text-success"
      : tone === "deduction" || tone === "contrib"
        ? "text-red-500"
        : "text-gray-900";
  const display = prefix ? `${prefix} ${value}` : value;
  return <span className={cn("text-sm font-medium", tint)}>{display}</span>;
};

interface IProps {
  runId: number;
  subtitle?: string;
  editable?: boolean;
  viewable?: boolean;
  showMarkPaid?: boolean;
  showReceiptSignatures?: boolean;
  periodLabel?: string;
  holdMode?: boolean;
  /** Item ids to hide (WPS-blocked rows, on the Active view only). */
  excludeItemIds?: number[];
  allowReprocess?: boolean;
  signatureStarted?: ReturnType<typeof usePayrollRunSocket>["signatureStarted"];
  signatureCompleted?: ReturnType<
    typeof usePayrollRunSocket
  >["signatureCompleted"];
  signatureFailed?: ReturnType<typeof usePayrollRunSocket>["signatureFailed"];
  /** Approved (Time Attendance) overtime requests for this run's period, keyed by userId. */
  approvedOtByUser?: Map<number, IOvertimeRequest[]>;
  /** Aggregate badge shown next to the Overtime column header. Omit to show no badge. */
  otSyncStatus?: "synced" | "pending";
  /** Lets HR manually set the overtime amount for a row. Calculate step only. */
  allowOvertimeOverride?: boolean;
}

export const EarningsTable = ({
  runId,
  subtitle,
  editable = false,
  viewable = false,
  showMarkPaid = false,
  showReceiptSignatures = false,
  periodLabel,
  holdMode = false,
  excludeItemIds = [],
  allowReprocess = false,
  signatureStarted = null,
  signatureCompleted = null,
  signatureFailed = null,
  approvedOtByUser,
  otSyncStatus,
  allowOvertimeOverride = false,
}: IProps) => {
  const { t: translate } = useTranslation(["company", "common"]);
  const subtitleText =
    subtitle ??
    translate(
      "company:payrolls.ot_auto_detected_edit_hours_bonuses_and_deductions_below",
    );
  const navigate = useNavigate();
  // Search, filters and page all live in the URL, namespaced per tab — the
  // Active and Hold tables share one url. See useTableParams.
  const tp = useEarningsParams(holdMode);
  const paymentMethodFilter = tp.filters.paymentMethod;
  const paidStatusFilter = tp.filters.paidStatus;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [overrideRow, setOverrideRow] = useState<IPayrollRunItem | null>(null);
  const [markPaid, { isLoading: isMarkingPaid }] = useMarkItemsPaidMutation();
  const [releaseItem, { isLoading: isReleasing }] = useReleaseItemMutation();
  const [reprocessHeld, { isLoading: isReprocessing }] =
    useReprocessHeldMutation();
  const [requestReceiptSignatures, { isLoading: isRequestingSig }] =
    useRequestReceiptSignaturesMutation();
  const isReceiptSigInProgress =
    !!signatureStarted &&
    signatureStarted.kind === "receipt" &&
    !signatureCompleted &&
    !signatureFailed;
  const lastSigJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    const done = signatureCompleted;
    if (!done || done.kind !== "receipt") return;
    if (lastSigJobIdRef.current === done.jobId) return;
    lastSigJobIdRef.current = done.jobId;
    const parts: string[] = [
      translate("company:payrolls.receipt_signature_sent_count", {
        count: done.sent,
      }),
    ];
    if (done.skipped)
      parts.push(
        translate("company:payrolls.receipt_signature_skipped_count", {
          count: done.skipped,
        }),
      );
    if (done.failed)
      parts.push(
        translate("company:payrolls.receipt_signature_failed_count", {
          count: done.failed,
        }),
      );
    toast.success(
      translate("company:payrolls.receipt_signature_requests_done", {
        summary: parts.join(", "),
      }),
    );
  }, [signatureCompleted, translate]);
  useEffect(() => {
    const fail = signatureFailed;
    if (!fail || fail.kind !== "receipt") return;
    if (lastSigJobIdRef.current === fail.jobId) return;
    lastSigJobIdRef.current = fail.jobId;
    toast.error(
      translate("company:payrolls.signature_job_failed", {
        error: fail.error,
      }),
    );
  }, [signatureFailed, translate]);
  const { download: downloadPayslip, isDownloading } = useDownloadPayslip();
  const [fetchAllItems, { isFetching: isFetchingAll }] =
    useLazyListItemsQuery();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadReport = async () => {
    setIsGeneratingPdf(true);
    try {
      const allItems: IPayrollRunItem[] = [];
      const baseParams = {
        runId,
        limit: 100,
        ...(holdMode
          ? { holdSalary: true }
          : {
              holdSalary: false,
              ...(excludeItemIds.length
                ? { excludeItemIds: excludeItemIds.join(",") }
                : {}),
            }),
      };
      let currentPage = 1;
      let totalPages = 1;
      do {
        const result = await fetchAllItems({
          ...baseParams,
          page: currentPage,
        }).unwrap();
        allItems.push(...(result.data ?? []));
        totalPages = result.meta?.totalPages ?? result.meta?.lastPage ?? 1;
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (!allItems.length) {
        toast.error(translate("company:payrolls.no_data_to_export"));
        return;
      }
      await generateEarningsPdf(allItems, {
        periodLabel:
          periodLabel ?? translate("company:payrolls.payroll_report"),
        filename: `earnings-${periodLabel?.replace(/\s+/g, "-") ?? runId}`,
      });
    } catch {
      toast.error(translate("company:payrolls.failed_to_generate_pdf"));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const { currentData, isFetching } = useListItemsQuery(
    {
      runId,
      page: tp.page,
      limit: PAGE_SIZE,
      ...(tp.committedSearch ? { search: tp.committedSearch } : {}),
      ...(paymentMethodFilter !== "all"
        ? { paymentMethod: paymentMethodFilter }
        : {}),
      ...(paidStatusFilter !== "all" ? { paidStatus: paidStatusFilter } : {}),
      ...(holdMode
        ? { holdSalary: true }
        : {
            holdSalary: false,
            ...(excludeItemIds.length
              ? { excludeItemIds: excludeItemIds.join(",") }
              : {}),
          }),
    },
    { skip: !runId, refetchOnMountOrArgChange: true },
  );

  const items = useMemo<IPayrollRunItem[]>(
    () => currentData?.data ?? [],
    [currentData],
  );

  const totalPages =
    currentData?.meta?.totalPages ?? currentData?.meta?.lastPage ?? 1;

  const handleRelease = async (itemId: number) => {
    try {
      await toast.promise(releaseItem({ runId, itemId }).unwrap(), {
        loading: translate("company:payrolls.releasing_salary"),
        success: translate("company:payrolls.salary_released"),
        error: (err: any) =>
          err?.data?.message ??
          err?.message ??
          translate("company:payrolls.failed_to_release"),
      });
    } catch {
      // toast handled
    }
  };

  const handleReprocess = async (itemIds?: number[]) => {
    try {
      await toast.promise(
        reprocessHeld({
          runId,
          ...(itemIds?.length ? { itemIds } : {}),
        }).unwrap(),
        {
          loading: translate(
            "company:payrolls.reprocessing_held_salary_and_generating_wps",
          ),
          success: (res) =>
            res.message ??
            `Reprocessed ${res.data?.releasedCount ?? 0} item(s), ${res.data?.filesGenerated ?? 0} WPS file(s) generated`,
          error: (err: any) => {
            const msg = err?.data?.message;
            return Array.isArray(msg)
              ? msg.join(", ")
              : (msg ?? translate("company:payrolls.failed_to_reprocess"));
          },
        },
      );
      setSelectedIds([]);
    } catch {
      // toast handled
    }
  };

  const columns: ColumnDef<IPayrollRunItem>[] = [
    {
      key: "employee",
      label: translate("common:labels.employee"),
      className: "w-[20%]",
      render: (r) => (
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            {r.employee.profileImage && (
              <AvatarImage
                src={r.employee.profileImage}
                alt={r.employee.fullName}
              />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
              {getInitials(r.employee.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {r.employee.fullName}
            </span>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400 truncate">
              <span>{r.employee.employeeCode}</span>
              {r.employee.countryName && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="truncate">{r.employee.countryName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "branchDept",
      label: translate("company:payrolls.branch_dept"),
      className: "w-[14%]",
      render: (r) => (
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-gray-900 truncate">
            {r.branchName ?? "—"}
          </span>
          <span className="text-[11px] text-gray-400 truncate">
            {r.departmentName ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "gross",
      label: translate("company:payrolls.gross_payroll"),
      className: "w-[10%]",
      render: (r) => {
        const n = toNumber(r.grossPayroll);
        return (
          <Cell
            value={n === null ? null : formatNumber(n)}
            prefix={r.currencyCode}
          />
        );
      },
    },
    {
      key: "overtime",
      label: (
        <span className="inline-flex items-center gap-1.5">
          {translate("company:payrolls.overtime")}
          {otSyncStatus === "pending" && (
            <Badge variant="warning">
              {translate("company:payrolls.sync_pending")}
            </Badge>
          )}
          {otSyncStatus === "synced" && (
            <Badge variant="success">
              {translate("company:payrolls.synced_from_attendance")}
            </Badge>
          )}
        </span>
      ),
      className: "w-[10%]",
      render: (r) => {
        const n = toNumber(r.overtimePay);
        const isZero = n === null || n === 0;
        const approvedOt = approvedOtByUser?.get(r.userId);
        const hasMismatch = isZero && !!approvedOt?.length;
        const approvedHours = hasMismatch
          ? approvedOt!.reduce(
              (sum, req) =>
                sum + (req.approvedHours ?? req.requestedHours ?? 0),
              0,
            )
          : 0;
        return (
          <div className="flex items-center gap-1.5">
            {hasMismatch ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="warning"
                      className="gap-1 cursor-help shrink-0"
                    >
                      <AlertTriangle size={11} />
                      {translate("company:payrolls.check_overtime")}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    {translate(
                      "company:payrolls.approved_overtime_not_included_hint",
                      { count: approvedHours },
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Cell
                value={isZero ? null : formatNumber(n as number)}
                prefix={n ? r.currencyCode : undefined}
              />
            )}
            {allowOvertimeOverride && (
              <PermissionGuard
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="hide"
              >
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setOverrideRow(r)}
                        className="text-gray-400 hover:text-primary shrink-0"
                      >
                        <PenLine size={12} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      {translate("company:payrolls.manual_overtime_override")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </PermissionGuard>
            )}
          </div>
        );
      },
    },
    {
      key: "totalDed",
      label: translate("company:payrolls.total_deductions"),
      className: "w-[10%]",
      render: (r) => {
        // Loss of Pay is stored separately from the deduction lines but feeds
        // the same Net Pay figure — fold it in so this column reconciles with
        // Net Pay and the Edit Item / payslip breakdown.
        const base = toNumber(r.totalDeductions);
        const lop = toNumber(r.lopAmount);
        const n =
          base === null && lop === null ? null : (base ?? 0) + (lop ?? 0);
        return (
          <Cell
            value={n === null ? null : formatNumber(n)}
            prefix={r.currencyCode}
            tone="deduction"
          />
        );
      },
    },
    {
      key: "contrib",
      label: translate("company:payrolls.contrib_short"),
      className: "w-[10%]",
      render: (r) => {
        const n = toNumber(r.totalStatutoryEmployee);
        return (
          <Cell
            value={n === null ? null : formatNumber(n)}
            prefix={r.currencyCode}
            tone="contrib"
          />
        );
      },
    },
    {
      key: "netPay",
      label: translate("company:payrolls.net_pay"),
      className: "w-[10%]",
      render: (r) => {
        const n = toNumber(r.netPay);
        return (
          <Cell
            value={n === null ? null : formatNumber(n)}
            prefix={r.currencyCode}
          />
        );
      },
    },
    {
      key: "paymentMethod",
      label: translate("company:payrolls.payment"),
      className: "w-[9%]",
      render: (r) =>
        r.paymentMethod ? (
          <Badge variant={PAYMENT_METHOD_VARIANT[r.paymentMethod] ?? "neutral"}>
            {PAYMENT_METHOD_LABELS[r.paymentMethod] ?? r.paymentMethod}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    ...(showMarkPaid && !holdMode
      ? [
          {
            key: "employeeStatus",
            label: translate("company:payrolls.payment_status"),
            className: "w-[8%]",
            render: (r: IPayrollRunItem) => (
              <Badge variant={statusToVariant(r.employeeStatus)}>
                {r.employeeStatus
                  ? r.employeeStatus.charAt(0) +
                    r.employeeStatus.slice(1).toLowerCase()
                  : translate("common:status.pending")}
              </Badge>
            ),
          } as ColumnDef<IPayrollRunItem>,
        ]
      : []),
    ...(showReceiptSignatures && !holdMode
      ? [
          {
            key: "receiptSignature",
            label: translate("company:payrolls.receipt"),
            className: "w-[8%]",
            render: (r: IPayrollRunItem) => {
              if (r.receiptSignatureStatus === "signed")
                return (
                  <span className="inline-flex items-center gap-1 rounded-xs bg-success-muted text-success px-2 py-0.5 text-[11px] font-semibold">
                    <Check size={11} />
                    {translate("my-space:ui.confirmed")}
                  </span>
                );
              if (r.receiptSignatureStatus === "pending")
                return (
                  <span className="inline-flex items-center gap-1 rounded-xs bg-warning-muted text-warning px-2 py-0.5 text-[11px] font-semibold">
                    <Clock size={11} />
                    {translate("my-space:ui.awaiting")}
                  </span>
                );
              return (
                <span className="text-muted-foreground text-[11px]">—</span>
              );
            },
          } as ColumnDef<IPayrollRunItem>,
        ]
      : []),
    ...(holdMode
      ? [
          {
            key: "heldAt",
            label: translate("company:payrolls.held_at"),
            className: "w-[12%]",
            render: (r: IPayrollRunItem) => (
              <span className="text-xs text-gray-600">
                {r.heldAt ? formatDateTime(r.heldAt) : "—"}
              </span>
            ),
          } as ColumnDef<IPayrollRunItem>,
          {
            key: "holdReason",
            label: translate("common:shared_ui.reason"),
            className: "w-[18%]",
            render: (r: IPayrollRunItem) => (
              <span
                className="text-xs text-gray-700 line-clamp-2"
                title={r.holdReason ?? ""}
              >
                {r.holdReason ?? "—"}
              </span>
            ),
          } as ColumnDef<IPayrollRunItem>,
        ]
      : []),
    ...(holdMode
      ? [
          {
            key: "action",
            label: "",
            className: "w-[14%]",
            render: (r: IPayrollRunItem) => (
              <div className="flex items-center gap-2">
                {editable && !r.holdSalary && (
                  <HoldRowButton runId={runId} itemId={r.id} />
                )}
                {(editable || viewable) &&
                  (editable ? (
                    <EditAction
                      tooltip={translate("company:payrolls.edit")}
                      onClick={() =>
                        navigate(
                          ROUTES.HOME.COMPANY.PAYROLL.RUN_ITEM(runId, r.id),
                        )
                      }
                    />
                  ) : (
                    <ViewAction
                      tooltip={translate("company:payrolls.view")}
                      onClick={() =>
                        navigate(
                          ROUTES.HOME.COMPANY.PAYROLL.RUN_ITEM(runId, r.id),
                        )
                      }
                    />
                  ))}
                {showMarkPaid && (
                  <Button
                    variant="outline-success"
                    size="icon-sm"
                    disabled={isDownloading}
                    aria-label={translate("company:payrolls.download_payslip")}
                    data-tooltip-id="hrm-tooltip"
                    data-tooltip-content={translate(
                      "company:payrolls.download_payslip",
                    )}
                    onClick={() => downloadPayslip(r.id)}
                  >
                    <Download size={14} />
                  </Button>
                )}
                <PermissionButton
                  section={PLATFORM_SECTION_ENUM.PAYROLL}
                  action="edit"
                  mode="hide"
                  variant="ghost"
                  size="sm"
                  className="text-info hover:text-info"
                  disabled={isReleasing || isReprocessing}
                  onClick={() => handleRelease(r.id)}
                  title={translate("company:payrolls.release_no_wps_regen")}
                >
                  <LockOpen size={16} />
                </PermissionButton>
                {allowReprocess && (
                  <PermissionButton
                    section={PLATFORM_SECTION_ENUM.PAYROLL}
                    action="edit"
                    mode="hide"
                    variant="ghost"
                    size="sm"
                    className="text-success hover:text-success"
                    disabled={isReleasing || isReprocessing}
                    onClick={() => handleReprocess([r.id])}
                    title={translate(
                      "company:payrolls.unhold_and_generate_wps",
                    )}
                  >
                    <RotateCw size={16} />
                  </PermissionButton>
                )}
              </div>
            ),
          } as ColumnDef<IPayrollRunItem>,
        ]
      : editable || viewable || showMarkPaid
        ? [
            {
              key: "action",
              label: "",
              className: "w-[8%]",
              render: (r: IPayrollRunItem) => (
                <div className="flex items-center gap-2">
                  {(editable || viewable) &&
                    (editable ? (
                      <EditAction
                        tooltip={translate("company:payrolls.edit")}
                        onClick={() =>
                          navigate(
                            ROUTES.HOME.COMPANY.PAYROLL.RUN_ITEM(runId, r.id),
                          )
                        }
                      />
                    ) : (
                      <ViewAction
                        tooltip={translate("company:payrolls.view")}
                        onClick={() =>
                          navigate(
                            ROUTES.HOME.COMPANY.PAYROLL.RUN_ITEM(runId, r.id),
                          )
                        }
                      />
                    ))}
                  {showMarkPaid && (
                    <Button
                      variant="outline-success"
                      size="icon-sm"
                      disabled={isDownloading}
                      aria-label={translate(
                        "company:payrolls.download_payslip",
                      )}
                      data-tooltip-id="hrm-tooltip"
                      data-tooltip-content={translate(
                        "company:payrolls.download_payslip",
                      )}
                      onClick={() => downloadPayslip(r.id)}
                    >
                      <Download size={14} />
                    </Button>
                  )}
                </div>
              ),
            } as ColumnDef<IPayrollRunItem>,
          ]
        : []),
  ];

  const handleMarkPaid = async () => {
    if (!selectedIds.length) return;
    const itemIds = selectedIds.map(Number);
    toast.promise(markPaid({ runId, itemIds }).unwrap(), {
      loading: translate("company:payrolls.marking_employees_as_paid_count", {
        count: itemIds.length,
      }),
      success: (res) => {
        setSelectedIds([]);
        return res.message ?? translate("company:payrolls.marked_as_paid");
      },
      error: (err) => {
        const msg = err?.data?.message;
        return Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? translate("company:payrolls.failed_to_mark_as_paid"));
      },
    });
  };

  const handleMarkAllPaid = () => {
    toast.promise(markPaid({ runId, all: true }).unwrap(), {
      loading: translate("company:payrolls.marking_all_employees_as_paid"),
      success: (res) =>
        res.message ?? translate("company:payrolls.all_marked_as_paid"),
      error: (err) => {
        const msg = err?.data?.message;
        return Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? translate("company:payrolls.failed"));
      },
    });
  };

  const handleRequestReceiptSignatures = async () => {
    const itemIds =
      selectedIds.length > 0 ? selectedIds.map(Number) : undefined;
    try {
      const result = await requestReceiptSignatures({
        runId,
        itemIds,
      }).unwrap();
      const total = result.data?.total ?? 0;
      toast.success(
        translate(
          "company:payrolls.sending_receipt_confirmation_requests_count",
          { count: total },
        ),
      );
      setSelectedIds([]);
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ??
              translate(
                "company:payrolls.failed_to_start_receipt_confirmation_requests",
              )),
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h3 className="text-base font-semibold text-gray-900">
            {holdMode
              ? periodLabel
                ? translate(
                    "company:payrolls.hold_salary_employees_title_with_period",
                    { period: periodLabel },
                  )
                : translate("company:payrolls.hold_salary_employees_title")
              : periodLabel
                ? translate(
                    "company:payrolls.employee_earnings_title_with_period",
                    { period: periodLabel },
                  )
                : translate("company:payrolls.employee_earnings_title")}
          </h3>
          <p className="text-xs text-gray-400">{subtitleText}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={isGeneratingPdf || isFetchingAll}
            onClick={handleDownloadReport}
          >
            <Download size={13} />
            {isGeneratingPdf || isFetchingAll
              ? translate("common:states.generating")
              : translate("company:payrolls.download_report")}
          </Button>
          {holdMode && allowReprocess && (
            <>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="hide"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={isReprocessing || items.length === 0}
                onClick={() => handleReprocess()}
              >
                <RotateCw size={13} />
                {translate("company:payrolls.reprocess_all_and_generate_wps")}
              </PermissionButton>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="hide"
                variant="primary"
                size="sm"
                className="gap-1.5"
                disabled={isReprocessing || selectedIds.length === 0}
                onClick={() => handleReprocess(selectedIds.map(Number))}
              >
                <RotateCw size={13} />
                {selectedIds.length > 0
                  ? translate("company:payrolls.reprocess_selected_count", {
                      count: selectedIds.length,
                    })
                  : translate("company:payrolls.reprocess_selected")}
              </PermissionButton>
            </>
          )}
          {showReceiptSignatures && !holdMode && (
            <PermissionButton
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="edit"
              mode="hide"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleRequestReceiptSignatures}
              disabled={isRequestingSig || isReceiptSigInProgress}
              isLoading={isRequestingSig || isReceiptSigInProgress}
            >
              {!(isRequestingSig || isReceiptSigInProgress) && (
                <PenLine size={13} />
              )}
              {isRequestingSig
                ? translate("common:states.sending")
                : isReceiptSigInProgress
                  ? translate("company:payrolls.sending_signatures_count", {
                      count: signatureStarted?.total ?? 0,
                    })
                  : selectedIds.length > 0
                    ? translate("company:payrolls.request_signatures_count", {
                        count: selectedIds.length,
                      })
                    : translate("company:payrolls.request_all_signatures")}
            </PermissionButton>
          )}
          {showMarkPaid && !holdMode && (
            <>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="disable"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleMarkAllPaid}
                disabled={
                  isMarkingPaid ||
                  items.every(
                    (i) => i.employeeStatus === EmployeePayrollStatusEnum.PAID,
                  )
                }
              >
                <CheckCircle size={13} />
                {translate("company:payrolls.mark_all_as_paid")}
              </PermissionButton>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="disable"
                variant="primary"
                size="sm"
                className="gap-1.5"
                onClick={handleMarkPaid}
                disabled={isMarkingPaid || selectedIds.length === 0}
              >
                <CheckCircle size={13} />
                {selectedIds.length > 0
                  ? translate("company:payrolls.mark_as_paid_count", {
                      count: selectedIds.length,
                    })
                  : translate("company:payrolls.mark_as_paid")}
              </PermissionButton>
            </>
          )}
          <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-72">
            <Search size={14} className="text-gray-400" />
            <input
              type="text"
              value={tp.search}
              onChange={(e) => tp.setSearch(e.target.value)}
              placeholder={translate(
                "company:payrolls.search_employee_name_status",
              )}
              className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
            />
          </div>
          {showMarkPaid && (
            <FilterButton
              filtersCount={tp.activeCount}
              onClick={() => setFilterOpen(true)}
              onReset={tp.reset}
            />
          )}
        </div>
      </div>

      {showMarkPaid && (
        <FiltersModal
          isOpen={filterOpen}
          initialFilters={tp.filters}
          defaultFilters={tp.defaults}
          onClose={() => setFilterOpen(false)}
          onApply={(f: IEarningsFilters) => tp.applyFilters(f)}
          renderFilters={({ filters, onChange }) => (
            <div className="flex flex-col gap-5">
              <Field>
                <FieldLabel>
                  {translate("company:payrolls.payment_type")}
                </FieldLabel>
                <Select
                  value={filters.paymentMethod}
                  onValueChange={(v) =>
                    onChange({
                      ...filters,
                      paymentMethod: v as PaymentMethodTypeEnum | "all",
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={translate("company:payrolls.all_types")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate("company:payrolls.all_types")}
                    </SelectItem>
                    <SelectItem value={PaymentMethodTypeEnum.WPS}>
                      {translate("company:payrolls.wps_label")}
                    </SelectItem>
                    <SelectItem value={PaymentMethodTypeEnum.BANK_TRANSFER}>
                      {translate("company:payrolls.bank_transfer")}
                    </SelectItem>
                    <SelectItem value={PaymentMethodTypeEnum.CASH}>
                      {translate("company:payrolls.cash")}
                    </SelectItem>
                    <SelectItem value={PaymentMethodTypeEnum.CHEQUE}>
                      {translate("company:payrolls.cheque")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>
                  {translate("company:payrolls.payment_status")}
                </FieldLabel>
                <Select
                  value={filters.paidStatus}
                  onValueChange={(v) =>
                    onChange({
                      ...filters,
                      paidStatus: v as "all" | PaidStatusEnum,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={translate("company:payrolls.all_status")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate("company:payrolls.all_status")}
                    </SelectItem>
                    <SelectItem value={PaidStatusEnum.PAID}>
                      {translate("company:payrolls.paid")}
                    </SelectItem>
                    <SelectItem value={PaidStatusEnum.NOT_PAID}>
                      {translate("company:payrolls.not_paid")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}
        />
      )}

      {overrideRow && (
        <OvertimeOverrideDialog
          runId={runId}
          row={overrideRow}
          approvedOt={approvedOtByUser?.get(overrideRow.userId)}
          onClose={() => setOverrideRow(null)}
        />
      )}

      <Table<IPayrollRunItem>
        noPadding
        data={items}
        columns={columns}
        loading={isFetching}
        pagination={{
          totalPages,
          currentPage: tp.page,
          onPageChange: tp.setPage,
        }}
        {...(holdMode && allowReprocess
          ? {
              selection: {
                selectedIds,
                onSelectionChange: setSelectedIds,
              },
            }
          : showMarkPaid || showReceiptSignatures
            ? {
                selection: {
                  selectedIds,
                  onSelectionChange: setSelectedIds,
                  disabledIds: showMarkPaid
                    ? items
                        .filter(
                          (i) =>
                            i.employeeStatus === EmployeePayrollStatusEnum.PAID,
                        )
                        .map((i) => String(i.id))
                    : [],
                },
              }
            : {})}
      />
    </div>
  );
};
